import * as React from 'react'
import { FC, useCallback, useMemo, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'
import { DataItem, PhoneLink } from '@sales-center/components'
import {
  ENTITY_TYPES,
  getFormattedAddress,
  type IEntityContactDTO,
} from '@sales-center/common'
import { ContactsDialog } from '@user/components/Contacts/ContactsDialog'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import EntityPropertyDetailsTitle from './PropertyDetails/EntityPropertyDetailsTitle'
import { usePropertyDetailsEnabled } from '@user/components/DetailPages/sections/EntityName/MeasureAI/hooks/usePropertyDetailsEnabled'

const getStyles = (theme: Theme) => ({
  root: {
    margin: `${theme.spacing(0.8)} ${theme.spacing(-2)} ${theme.spacing(
      -1,
    )} ${theme.spacing(-2)}`,
    padding: `0 ${theme.spacing(2)} ${theme.spacing(1.2)} ${theme.spacing(2)}`,
  },

  dataItemContainer: {
    '&>div:not(:first-child)': {
      marginLeft: theme.spacing(5.6),
    },
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.2),
  },

  button: {
    alignSelf: 'end',
    color: '#005AA3',
  },

  link: {
    color: '#005AA3',
    cursor: 'pointer',
    fontSize: '13px',
  },

  PropLink: {
    color: '#8E24AA',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '400',
    marginLeft: '8px',
    marginRight: '29px',
  },

  title: {
    color: '#33383c',
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    lineHeight: '36px',
    margin: (theme: Theme) => theme.spacing(0),
  },
})

interface Props {
  canUpdate: boolean
  contact: IEntityContactDTO
  entityType: ENTITY_TYPES
  entityId: number
  hasValidPropertyAddress?: boolean
}

export const EntityPrimaryContactDrawer: FC<Props> = (props) => {
  const {
    canUpdate,
    contact,
    contact: { address, address2, city, state, postalCode, phone, phoneExt },
    entityId,
    entityType,
  } = props

  // ========== S T A T E ==========
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [showSecondaryContacts, setShowSecondaryContacts] =
    useState<boolean>(false)

  const toggleDrawer = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  const fullAddress = useMemo(() => {
    return getFormattedAddress(address, address2, city, state, postalCode)
  }, [address, address2, city, postalCode, state])

  // ========== E V E N T S ==========
  const handleChangePrimaryContactClick = useCallback(() => {
    setShowSecondaryContacts(true)
  }, [])

  const handleCloseSecondaryContacts = useCallback(() => {
    setShowSecondaryContacts(false)
  }, [])

  const drawerStateIndicator = isOpen ? '-' : '+'

  const primaryEmail = contact.emailAddresses.find((x) => x.isPrimary)

  const contactName =
    `${contact.firstName} ${contact.lastName}`.trim() || contact.contactName
  const theme = useTheme()
  const styles = getStyles(theme)

  const measureAIEnabled = usePropertyDetailsEnabled()

  return (
    <>
      <Box mt={'13px'}>
        {measureAIEnabled && <EntityPropertyDetailsTitle {...props} />}

        <Typography
          display="inline"
          sx={styles.link}
          onClick={toggleDrawer}
          data-test-id={`entity-primary-contact-drawer-toggle-${entityId}`}
        >
          View Primary Contact {drawerStateIndicator}
        </Typography>

        <Collapse in={isOpen} timeout="auto">
          <Box
            py={2}
            sx={styles.root}
            style={{
              backgroundColor: isOpen ? '#f4f8fa' : theme.palette.common.white,
              transition: isOpen ? 'background-color 1s' : 'none',
            }}
          >
            <Box display="flex" sx={styles.header}>
              <Typography variant="h3" sx={styles.title}>
                Primary Contact
              </Typography>
              <Button
                sx={styles.button}
                onClick={handleChangePrimaryContactClick}
                variant="text"
                data-test-id={`entity-primary-contact-drawer-manage-contact-button-${entityId}`}
              >
                Manage Contacts
              </Button>
            </Box>

            <Grid container sx={styles.dataItemContainer}>
              <DataItem title="Name:">{contactName}</DataItem>
              <DataItem title="Phone:">
                <PhoneLink
                  phone={phone}
                  phoneExt={phoneExt}
                  dataTestId={`entity-primary-contact-drawer-manage-phone-button-${entityId}`}
                />
              </DataItem>
              <DataItem isEmpty={!primaryEmail} title="Primary Email:">
                <a
                  href={`mailto:${primaryEmail?.emailAddress}`}
                  target="_blank"
                  rel="noreferrer"
                  data-test-id={`entity-primary-contact-drawer-manage-email-button-${entityId}`}
                >
                  {primaryEmail?.emailAddress}
                </a>
              </DataItem>
              <DataItem title="Address:" isEmpty={!fullAddress}>
                {fullAddress}
              </DataItem>
            </Grid>
          </Box>
        </Collapse>
      </Box>
      {/* ========== S E C O N D A R Y   C O N T A C T S ========== */}
      <ContactsDialog
        canUpdateEntity={canUpdate}
        entityId={entityId}
        entityType={entityType}
        open={showSecondaryContacts}
        onClose={handleCloseSecondaryContacts}
      />
    </>
  )
}

EntityPrimaryContactDrawer.displayName = 'EntityPrimaryContactDrawer'
